import {
  ExpandOutlined,
  LeftOutlined,
  PictureOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Button, Image, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { globalMapStatusStyle } from "../helpers/status-style"
import type { EntityQuickView } from "../redux/global-map.types"

const { Text } = Typography

interface QuickViewHeroProps {
  record: EntityQuickView
}

/**
 * The panel's hero: the record's own photos, its status badge over them, and
 * a full-size preview.
 *
 * Photos come from `media.images` — real `core.Attachment` rows on this
 * record (`_serialize_media`, backend), never a stock or generated image. A
 * record with none keeps the same rectangle and shows a plain placeholder
 * rather than collapsing, so the panel's layout does not change shape
 * between records.
 */
export function QuickViewHero({ record }: QuickViewHeroProps) {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)

  const images = record.media?.images ?? []
  const total = images.length
  const current = images[Math.min(index, Math.max(total - 1, 0))]

  const step = (delta: number) =>
    setIndex((value) => (value + delta + total) % total)

  return (
    <div
      className="relative h-[168px] w-full shrink-0 overflow-hidden rounded-t-[10px] bg-slate-100"
      data-testid="global-map-quickview-hero"
    >
      {current?.url ? (
        <Image
          src={current.url}
          alt={current.name ?? ""}
          // `object-cover` on a fixed-height box: the photo fills the panel
          // width and is cropped, never stretched out of its own aspect.
          className="!h-[168px] !w-full object-cover"
          wrapperClassName="!block !h-[168px] !w-full"
          preview={{ mask: <ExpandOutlined aria-hidden /> }}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-2 bg-gradient-to-b from-slate-50 to-slate-100">
          <PictureOutlined
            aria-hidden
            className="!text-[26px] text-slate-300"
          />
          <Text className="text-[12px] text-slate-400">
            {t("globalMap.quickView.media.empty")}
          </Text>
        </div>
      )}

      <span
        className="absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-medium shadow-sm"
        style={globalMapStatusStyle(record.entity_type, record.status_label)}
      >
        {record.status_label}
      </span>

      {total > 1 && (
        <>
          <Button
            shape="circle"
            size="small"
            aria-label={t("globalMap.quickView.media.previous")}
            icon={<LeftOutlined />}
            onClick={() => step(-1)}
            className="!absolute left-2 top-1/2 -translate-y-1/2 border-none bg-white/90 shadow"
          />
          <Button
            shape="circle"
            size="small"
            aria-label={t("globalMap.quickView.media.next")}
            icon={<RightOutlined />}
            onClick={() => step(1)}
            className="!absolute right-2 top-1/2 -translate-y-1/2 border-none bg-white/90 shadow"
          />
          <span className="absolute bottom-3 right-3 rounded-full bg-slate-900/70 px-2 py-0.5 text-[11px] font-medium text-white">
            {t("globalMap.quickView.media.counter", {
              current: Math.min(index + 1, total),
              total: record.media.count || total
            })}
          </span>
        </>
      )}
    </div>
  )
}

export default QuickViewHero
